// ---------------------------------------------
// SRVMarketplaceOverview.jsx
// Mit eigener Listing-Kennzeichnung & modernen Badges (robust)
// ---------------------------------------------
import React, { useEffect, useMemo, useState } from "react";
import api from "../axiosSetup";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "../css/marketplace-dark.css";
import SRVListingDetailModal from "./srvListingDetailModal";

/* --------------------------------------------
   Modernes Status-Badge (mit robustem Fallback)
--------------------------------------------- */
const LISTING_STATUS_STYLES = {
  aktiv: {
    bg: "linear-gradient(180deg, #022c22, #064e3b)",
    border: "#22c55e",
    text: "#86efac",
    icon: (
      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    label: "Aktiv",
  },
  pausiert: {
    bg: "linear-gradient(180deg, #0b1220, #111827)",
    border: "#60a5fa",
    text: "#93c5fd",
    icon: (
      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M9 7v10M15 7v10" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    label: "Pausiert",
  },
  abgelaufen: {
    bg: "linear-gradient(180deg, #111827, #1f2937)",
    border: "#9ca3af",
    text: "#e5e7eb",
    icon: (
      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
        <path d="M12 8v4l3 3" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    label: "Abgelaufen",
  },
  archiviert: {
    bg: "linear-gradient(180deg, #1e1b4b, #312e81)",
    border: "#8b5cf6",
    text: "#ddd6fe",
    icon: (
      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 7h16M7 7v10h10V7" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    label: "Archiviert",
  },
};

function StatusChip({ status }) {
  // robust normalisieren
  const key = (status ?? "").toString().trim().toLowerCase();
  const meta = LISTING_STATUS_STYLES[key] ?? {
    bg: "linear-gradient(180deg,#111827,#1f2937)",
    border: "#a5b4fc",
    text: "#c7d2fe",
    icon: (
      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    label: (status ?? "Unbekannt").toString(),
  };

  return (
    <span
      style={{
        display: "inline-flex",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        border: `1px solid ${meta.border}`,
        background: meta.bg,
        color: meta.text,
        fontWeight: 700,
        fontSize: 12,
        alignItems: "center",
        userSelect: "none",
      }}
      title={meta.label}
    >
      {meta.icon}
      {meta.label}
    </span>
  );
}

/* --------------------------------------------
   Kleine InfoChips (dark) – inkl. Fallback
--------------------------------------------- */
function InfoChip({ color = "slate", children }) {
  const palettes = {
    slate: { bg: "rgba(148,163,184,.15)", border: "rgba(148,163,184,.4)", text: "#e5e7eb" },
    amber: { bg: "rgba(245,158,11,.15)", border: "rgba(245,158,11,.4)", text: "#fcd34d" },
    lime:  { bg: "rgba(132,204,22,.15)", border: "rgba(132,204,22,.4)", text: "#bef264" },
    sky:   { bg: "rgba(14,165,233,.15)", border: "rgba(14,165,233,.4)", text: "#7dd3fc" },
  };
  const p = palettes[color] ?? palettes.slate;

  return (
    <span
      style={{
        display: "inline-flex",
        padding: "6px 10px",
        borderRadius: 999,
        background: p.bg,
        border: `1px solid ${p.border}`,
        color: p.text,
        fontWeight: 600,
        fontSize: 12,
      }}
    >
      {children}
    </span>
  );
}

const STATUS_OPTIONS = [
  { value: "aktiv",      label: "Aktiv" },
  { value: "alle",       label: "Alle" },
  { value: "pausiert",   label: "Pausiert" },
  { value: "abgelaufen", label: "Abgelaufen" },
  { value: "archiviert", label: "Archiviert" },
];

const LS_HIDDEN = "swap_hidden_listings";
function loadHidden() {
  try { return new Set(JSON.parse(localStorage.getItem(LS_HIDDEN) || "[]")); }
  catch { return new Set(); }
}
function saveHidden(set) {
  localStorage.setItem(LS_HIDDEN, JSON.stringify([...set]));
}

export default function SRVMarketplaceOverview() {
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("aktiv");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const [openDetail, setOpenDetail] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);

  const [hiddenIds, setHiddenIds] = useState(loadHidden);
  const [showHidden, setShowHidden] = useState(false);

  const token = localStorage.getItem("authToken");

  const hideId = (id) => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      next.add(id);
      saveHidden(next);
      return next;
    });
  };

  const unhideAll = () => {
    setHiddenIds(new Set());
    saveHidden(new Set());
    setShowHidden(false);
  };

  // ----------------------------------------------------------
  // LISTINGS LADEN
  // ----------------------------------------------------------
  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (q.trim()) params.set("q", q.trim());

      const res = await api.get(`/api/swap-marketplace/listings?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Fehler beim Laden der Tauschbörse.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hiddenCount = useMemo(() => data.filter(l => hiddenIds.has(l.listingId)).length, [data, hiddenIds]);
  const filtered = useMemo(
    () => showHidden ? data : data.filter(l => !hiddenIds.has(l.listingId)),
    [data, hiddenIds, showHidden]
  );

  const openModal = (l) => {
    setSelectedListing(l);
    setOpenDetail(true);
  };
  const closeModal = () => {
    setSelectedListing(null);
    setOpenDetail(false);
  };

  return (
    <div className="container my-4 marketplace-dark">
      {/* --------------------------------
          Suchleiste / Filter
      -------------------------------- */}
      <div
        className="d-flex flex-column flex-md-row gap-2 mb-3"
        style={{
          padding: 12,
          borderRadius: 12,
          border: "1px solid rgba(148,163,184,.25)",
          backdropFilter: "blur(2px)",
        }}
      >
        <div className="flex-grow-1">
          <label className="form-label text-white-50">Suche</label>
          <input
            className="form-control"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchListings()}
            placeholder="Nach Einsatz, Besitzer oder Grund suchen…"
            style={{
              background: "rgba(15,23,42,.6)",
              color: "#e5e7eb",
              borderColor: "rgba(148,163,184,.35)",
            }}
          />
        </div>

        <div style={{ minWidth: 200 }}>
          <label className="form-label text-white-50">Status</label>
          <select
            className="form-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{
              background: "rgba(15,23,42,.6)",
              color: "#e5e7eb",
              borderColor: "rgba(148,163,184,.35)",
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <button className="btn btn-primary mt-4 mt-md-0" onClick={fetchListings}>
          Aktualisieren
        </button>
      </div>

      {/* --------------------------------
          Ergebnisliste
      -------------------------------- */}
      {loading ? (
        <p className="text-white-50">Lade Tauschbörse…</p>
      ) : filtered.length === 0 ? (
        <div
          className="text-white"
          style={{
            border: "1px solid rgba(148,163,184,.25)",
            borderRadius: 12,
            padding: "16px 20px",
          }}
        >
          Keine Einträge gefunden.
        </div>
      ) : (
        <div className="row g-3">
          {filtered.map((l) => {
            const createdAt = l.createdAt ? dayjs(l.createdAt).format("DD.MM.YYYY HH:mm") : "—";
            const expiresAt = l.expiresAt ? dayjs(l.expiresAt).format("DD.MM.YYYY HH:mm") : null;

            return (
              <div className="col-12" key={l.listingId}>
                <div
                  className="card bg-transparent border-1"
                  style={{
                    borderColor: "rgba(255,255,255,0.12)",
                    borderRadius: 14,
                    background: "transparent",
                  }}
                >
                  <div className="card-body text-white">

                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h5 className="mb-1">{l.assignmentName}</h5>
                        <div className="text-white-50">
                          Anbieter: <strong className="text-white">{l.ownerName}</strong>
                        </div>
                      </div>

                      <div className="d-flex flex-column gap-2 align-items-end">
                        {/* robust gegen undefined/sonderzeichen */}
                        <StatusChip status={l.status ?? l.dbStatus} />

                        {l.isOwnListing && (
                          <InfoChip color="lime">Eigenes Angebot</InfoChip>
                        )}

                        {l.hasMyActiveOffer && (
                          <InfoChip color="amber">
                            Mein Gegenangebot aktiv
                          </InfoChip>
                        )}

                        <button
                          title="Ausblenden"
                          aria-label="Angebot ausblenden"
                          onClick={() => hideId(l.listingId)}
                          style={{
                            background: "none", border: "1px solid rgba(255,255,255,0.2)",
                            color: "rgba(255,255,255,0.45)", borderRadius: 6,
                            padding: "2px 7px", fontSize: 13, cursor: "pointer",
                            lineHeight: 1.4,
                          }}
                        >
                          ✕ Ausblenden
                        </button>
                      </div>
                    </div>

                    {/* Reason */}
                    {l.reason && (
                      <div className="mt-2">
                        <strong>Grund:</strong> {l.reason}
                      </div>
                    )}

                    {/* Dates */}
                    {Array.isArray(l.dates) && l.dates.length > 0 && (
                      <div className="mt-2">
                        <div className="fw-semibold">Termine:</div>
                        <ul className="m-0 ps-3">
                          {l.dates.map((d, i) => (
                            <li key={i}>
                              {dayjs(d.workDate).format("DD.MM.YYYY")} –{" "}
                              {String(d.timeFrom).slice(0, 5)}–{String(d.timeTo).slice(0, 5)} Uhr
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Info Chips */}
                    <div className="mt-3 d-flex flex-wrap gap-2">
                      <InfoChip color="sky">Angebote: {l.offersCount ?? 0}</InfoChip>
                      <InfoChip color="slate">Erstellt: {createdAt}</InfoChip>
                      {expiresAt && <InfoChip color="amber">Läuft ab: {expiresAt}</InfoChip>}
                    </div>

                    {/* Hinweis eigene Listings */}
                    {l.isOwnListing && (
                      <div
                        className="mt-2"
                        style={{
                          padding: "8px 10px",
                          background: "rgba(139,92,246,.15)",
                          border: "1px solid rgba(139,92,246,.45)",
                          color: "#ddd6fe",
                          borderRadius: 10,
                        }}
                      >
                        Für eigene Angebote können keine Gegenangebote erstellt werden.
                      </div>
                    )}

                    {/* Hinweis eigenes Offer */}
                    {l.hasMyActiveOffer && !l.isOwnListing && (
                      <div
                        className="mt-2"
                        style={{
                          padding: "8px 10px",
                          background: "rgba(245,158,11,.12)",
                          border: "1px solid rgba(245,158,11,.35)",
                          color: "#fde68a",
                          borderRadius: 10,
                        }}
                      >
                        Du hast bereits ein aktives Gegenangebot zu diesem Angebot.
                      </div>
                    )}

                    {/* Button */}
                    <div className="mt-3">
                      <button
                        className="btn btn-outline-light btn-sm"
                        onClick={() => openModal(l)}
                        style={{ borderRadius: 10 }}
                      >
                        Angebot anschauen
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Ausgeblendete-Bar */}
          {hiddenCount > 0 && (
            <div className="col-12">
              <div
                style={{
                  padding: "10px 14px", borderRadius: 10, fontSize: 13,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.04)",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  color: "rgba(255,255,255,0.5)",
                }}
              >
                <span>{hiddenCount} Angebot{hiddenCount !== 1 ? "e" : ""} ausgeblendet</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    style={{ fontSize: 12, padding: "2px 10px" }}
                    onClick={() => setShowHidden(v => !v)}
                  >
                    {showHidden ? "Wieder verstecken" : "Einblenden"}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    style={{ fontSize: 12, padding: "2px 10px" }}
                    onClick={unhideAll}
                  >
                    Alle zurücksetzen
                  </button>
                </div>
              </div>
            </div>
          )}

          {openDetail && selectedListing && (
            <SRVListingDetailModal
              open={openDetail}
              onClose={closeModal}
              listing={selectedListing}
              onOfferCreated={() => {
                closeModal();
                fetchListings();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}